import { useEffect, useRef, ReactElement, Fragment } from "react";
import { MessageVariants } from "@twilio-paste/core/chat-log";
import { Avatar } from "@twilio-paste/core/avatar";
import { Box } from "@twilio-paste/core/box";
import { Spinner } from "@twilio-paste/core/spinner";

import {
  ChatLog,
  ChatBookend,
  ChatBookendItem,
  ChatBubble,
  ChatMessage,
  ChatMessageMeta,
  ChatMessageMetaItem,
} from "@twilio-paste/core/chat-log";

import { MarkdownPreview } from "./sub";

import { copy, types } from "./definitions";
import { ChatAttachmentPreview } from "./sub";
import { sortChatMessagesByRelativeTime } from "./utils";
import styles from "./CompositeChatLog.module.css";
import {
  Attachment,
  MESSAGE_DATE_FORMAT,
  MESSAGE_TIME_FORMAT,
} from "@twiliointernal/e2-commons-shared-definitions";
import { DateTime } from "@twiliointernal/e2-commons-shared-utils";

const CompositeChatLog = ({
  loading = true,
  chatMessages = [],
  custom,
}: types.CompositeChatLogProps): ReactElement => {
  const customStyles = custom?.styles;

  // custom behaviors
  let autoScrollForAuthor: boolean | undefined;
  let autoScrollForRecipient: boolean | undefined;
  let messageTimeFormat: string | undefined;
  let messageDateFormat: string | undefined;
  let formatDatePastYesterday: string | undefined;

  const customBehaviors = custom?.behaviors;

  if (customBehaviors) {
    ({
      autoScrollForAuthor = true,
      autoScrollForRecipient = true,
      messageTimeFormat = MESSAGE_TIME_FORMAT,
      messageDateFormat = MESSAGE_DATE_FORMAT,
      formatDatePastYesterday,
    } = customBehaviors);
  }

  // custom components
  const customComponents = custom?.components;
  const CustomChatMessage = customComponents?.ChatMessage?.component;
  const customChatMessageProps = customComponents?.ChatMessage?.additionalProps;

  const ChatBubbleContent =
    customComponents?.ChatBubbleContent?.component || MarkdownPreview;
  const chatbubbleAdditionalProps =
    customComponents?.ChatBubbleContent?.additionalProps;

  const messageToScrollTo = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (chatMessages.length === 0) {
      return;
    }

    const { author } = chatMessages[chatMessages.length - 1];

    if (author.isAuthor) {
      if (autoScrollForAuthor) {
        scrollToLastMessage();
      }
    } else {
      if (autoScrollForRecipient) {
        scrollToLastMessage();
      }
    }
  }, [chatMessages]);

  const scrollToLastMessage = (): void => {
    // alignTop property should be false to ensure page doesn't scroll to bottom
    messageToScrollTo?.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleSetScrollableElement = (element: HTMLDivElement): void => {
    if (!messageToScrollTo.current) {
      messageToScrollTo.current = element;
      scrollToLastMessage();
    }
  };

  const renderAttachments = (
    attachments: Attachment[],
    variant: MessageVariants
  ): ReactElement[] =>
    attachments.map((attachment) => (
      <ChatAttachmentPreview
        key={attachment.name}
        attachment={attachment}
        variant={variant}
      />
    ));

  const renderChatMessage = (chatMessage: types.ChatMessage): ReactElement => {
    if (CustomChatMessage) {
      return (
        <CustomChatMessage
          chatMessage={chatMessage}
          {...customChatMessageProps}
        />
      );
    }

    const {
      attachments,
      author: { displayName, isAuthor, isEndUser },
      body,
      createdAt,
      htmlBody,
    } = chatMessage;

    const dateTime = new DateTime(createdAt);
    const formattedDate = dateTime.format(
      messageDateFormat || MESSAGE_DATE_FORMAT
    );
    const formattedTime = dateTime.format(
      messageTimeFormat || MESSAGE_TIME_FORMAT
    );

    const authorDisplayName = isAuthor
      ? copy.messages.endUserAlias
      : displayName;
    const variant = isAuthor ? "outbound" : "inbound";

    /**
     * Markdown support in zendesk is only available to agents.
     * Therefore the HTML body is correct only for agents
     * @see https://support.zendesk.com/hc/en-us/community/posts/360038476034-Markdown-for-customers
     */
    const markdownValue = isEndUser ? body : htmlBody;
    const avatarProps = {
      name: displayName,
    };

    return (
      <ChatMessage variant={variant}>
        <ChatBubble>
          <ChatBubbleContent
            value={markdownValue}
            isHtml={!isEndUser}
            {...chatbubbleAdditionalProps}
          />
        </ChatBubble>

        {!!attachments.length && renderAttachments(attachments, variant)}

        <ChatMessageMeta
          aria-label={copy.messages.eventLabel(
            authorDisplayName,
            formattedDate
          )}
        >
          <ChatMessageMetaItem>
            <Avatar size="sizeIcon20" {...avatarProps} />
            {copy.messages.timeStamp(authorDisplayName, formattedDate)}
          </ChatMessageMetaItem>
        </ChatMessageMeta>
      </ChatMessage>
    );
  };

  const renderChatLog = (): ReactElement[] => {
    const messagesRenderedByRelativeTime = sortChatMessagesByRelativeTime(
      chatMessages,
      formatDatePastYesterday
    );

    const renderedChatMessages = Object.keys(
      messagesRenderedByRelativeTime
    ).map((timeLineKey, index) => (
      <Fragment key={index}>
        <ChatBookend>
          <ChatBookendItem>{timeLineKey}</ChatBookendItem>
          {index === 0 && (
            <ChatBookendItem>
              <strong>{copy.messages.startBookend}</strong>
            </ChatBookendItem>
          )}
        </ChatBookend>

        {messagesRenderedByRelativeTime[timeLineKey].map((chatMessage) =>
          renderChatMessage(chatMessage)
        )}
      </Fragment>
    ));

    return renderedChatMessages;
  };

  return (
    <Box
      className={styles.compositeChatLogContainer}
      height="size50"
      backgroundColor="colorBackgroundBody"
      borderColor="colorBorderWeak"
      {...customStyles?.chatLogContainer}
    >
      <ChatLog>
        {loading ? (
          <Box className={styles.chatLoadingContainer}>
            <Spinner title={copy.loadingTitle} decorative size="sizeIcon80" />
          </Box>
        ) : (
          <>
            {chatMessages?.length > 0 && renderChatLog()}
            {/* Placeholder div to scroll to last message */}
            <Box as="div" ref={handleSetScrollableElement} />
          </>
        )}
      </ChatLog>
    </Box>
  );
};

export default CompositeChatLog;
